import React, { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import { authApi, type LoginData, type RegisterData } from '../services/api';

// 用户信息类型定义
export interface User {
  userId: string;
  username: string;
  email?: string;
  phone?: string;
  languagePreference: string;
}

// 认证状态类型定义
export interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

// 动作类型
type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_START' }
  | { type: 'REGISTER_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: { user: User; token: string } }
  | { type: 'CLEAR_ERROR' };

// 初始状态
const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
};

// 创建Context
type AuthContextType = AuthState & {
  login: (data: LoginData) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  clearError: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Reducer函数
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
    case 'REGISTER_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
    case 'SET_USER':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        error: null,
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Provider组件
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 从localStorage加载用户信息
  useEffect(() => {
    const loadUser = () => {
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          dispatch({ type: 'SET_USER', payload: { user, token } });
        } catch (error) {
          // 数据解析失败，清除localStorage
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        }
      }
    };

    loadUser();
  }, []);

  // 保存用户信息到localStorage
  const saveUser = (user: User, token: string) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user', JSON.stringify(user));
  };

  // 清除localStorage中的用户信息
  const clearUser = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  };

  // 登录功能
  const login = async (data: LoginData) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await authApi.login(data);
      const user: User = {
        userId: response.userId,
        username: data.username,
        languagePreference: 'en', // 默认语言，后续可以从API获取
      };
      saveUser(user, response.token);
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token: response.token } });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // 注册功能
  const register = async (data: RegisterData) => {
    dispatch({ type: 'REGISTER_START' });
    try {
      const response = await authApi.register(data);
      const user: User = {
        userId: response.userId,
        username: data.username,
        email: data.email,
        phone: data.phone,
        languagePreference: data.languagePreference,
      };
      saveUser(user, response.token);
      dispatch({ type: 'REGISTER_SUCCESS', payload: { user, token: response.token } });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'REGISTER_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // 登出功能
  const logout = () => {
    dispatch({ type: 'LOGOUT' });
    clearUser();
  };

  // 清除错误
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// 自定义Hook，用于访问AuthContext
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
